import React, { useCallback, useEffect, useState } from 'react';
import SavedCardDisplay from './SavedCardDisplay';
import CardInput from './CardInput';
import SaveCardButton from './SaveCardButton';
import './managePaymentsStyles.css';
import { getClinicId } from '../../utils/config';
import FormatUrl from '../../utils/UrlFormatter';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const ManagePaymentsSection = ({ user }) => {
  const clinicId = getClinicId(user);
  const { t } = useTranslation();

  const [cardList, setCardList] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);

  const [estimationResult, setEstimationResult] = useState(null);
  const [loadingEstimation, setLoadingEstimation] = useState(false);

  const fetchSavedCards = useCallback(async () => {
    try {
      const profileId = user?.profile_id;
      if (!profileId) return;

      const csrfRes = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const { csrfToken } = await csrfRes.json();

      const res = await fetch(
        FormatUrl(`/payments/get-saved-card/?profile_id=${profileId}`),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
        }
      );

      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) {
        setCardList([]);
      } else {
        setCardList(data);
      }
    } catch (err) {
      console.error("Error fetching saved cards:", err);
      toast.error("Failed to fetch cards");
      setCardList([]);
    } finally {
      setLoadingCards(false);
    }
  }, [user?.profile_id]);

  const fetchInvoiceEstimation = useCallback(async () => {
    if (!clinicId) return;
    setLoadingEstimation(true);

    try {
      const csrfRes = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const { csrfToken } = await csrfRes.json();

      const formData = new URLSearchParams();
      formData.append('clinic_id', clinicId);
      formData.append('month_date', new Date().toISOString().split('T')[0]);
      formData.append('from_day', 1);

      const res = await fetch(FormatUrl('/invoice_estimation/'), {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        setEstimationResult(data);
      } else {
        throw new Error(data?.error || 'Estimation fetch failed');
      }
    } catch (err) {
      console.error('Invoice Estimation Error:', err);
      toast.error('Failed to fetch invoice estimation');
    } finally {
      setLoadingEstimation(false);
    }
  }, [clinicId]);

  useEffect(() => {
    fetchSavedCards();
    fetchInvoiceEstimation();
  }, [fetchSavedCards, fetchInvoiceEstimation]);

  return (
    <div className="page-wrapper">
      <div className="info-banner">
        <h1>{t('manage_card.page_title')}</h1>
        {/* <p>{t('manage_card.description_line1')}</p>
        <p>{t('manage_card.description_line2')}</p> */}
      </div>

      <div className="main-content">
        <div className="manage-payments-container">
          <h2 className="manage-payments-heading">{t('manage_card.section_heading')}</h2>

          <div className="manage-payments-sections">
            <div className="section-box">
              <h3>
                {t('manage_card.saved_card_title')}
                <span style={{ fontWeight: 'normal', fontSize: '0.9em' }}>
                  {t('manage_card.saved_card_subtitle')}
                </span>
              </h3>
              <SavedCardDisplay
                user={user}
                cardList={cardList}
                loading={loadingCards}
                onCardRemoved={fetchSavedCards}
              />
            </div>

            <div className="section-box">
              <h3>{t('manage_card.add_card_title')}</h3>
              <div className="card-entry-section">
                <CardInput />
                <SaveCardButton
                  user={user}
                  clinicId={clinicId}
                  onCardSaved={fetchSavedCards}
                />
              </div>
            </div>
          </div>

          {/* Invoice Estimation Section */}
          <div className="section-box invoice-estimation-box">
  <h3>{t('manage_card.invoice_estimation_title')}</h3>

  {loadingEstimation ? (
    <p>{t('manage_card.invoice_loading')}</p>
  ) : estimationResult ? (
    <>
      <div className="invoice-summary-grid">
        <div className="label">{t('manage_card.billing_start')}</div>
        <div className="value">{estimationResult.billing_period_start}</div>

        <div className="label">{t('manage_card.billing_end')}</div>
        <div className="value">{estimationResult.billing_period_end}</div>

        <div className="label">{t('manage_card.invoice_date')}</div>
        <div className="value">{estimationResult.invoice_date}</div>

        <div className="label">{t('manage_card.platform_fees')}</div>
        <div className="value">${Number(estimationResult.platform_onboarding_fees).toFixed(2)}</div>

        <div className="label">{t('manage_card.num_active_practitioners')}</div>
        <div className="value">{estimationResult.num_active_doctors}</div>

        <div className="label">{t('manage_card.practitioner_subscription_cost')}</div>
        <div className="value">
          {estimationResult.num_active_doctors >= 1 && (
            <div className="amount-breakup">
              <div>1 × ${Number(estimationResult.first_doctor_charge).toFixed(2)} = ${Number(estimationResult.first_doctor_charge).toFixed(2)}</div>
              {estimationResult.num_active_doctors > 1 && (
                <div>
                  {estimationResult.num_active_doctors - 1} × ${Number(estimationResult.additional_doctors_charge_per_doctor).toFixed(2)} = ${(Number(estimationResult.additional_doctors_charge_per_doctor) * (estimationResult.num_active_doctors - 1)).toFixed(2)}
                </div>
              )}
              <div style={{ fontWeight: 'bold', marginTop: '0.5rem' }}>
                {t('manage_card.total_practitioner_cost')}: ${Number(estimationResult.amount).toFixed(2)}
              </div>
            </div>
          )}
        </div>

        <div className="label">{t('manage_card.value_added_services')}</div>
        <div className="value">${Number(estimationResult.value_added_service_extra_cost).toFixed(2)}</div>
        
        <div className="label">{t('manage_card.sub_total')}</div>
        <div className="value">${Number(estimationResult.sub_total).toFixed(2)}</div>

        <div className="label">{t('manage_card.tax')}</div>
        <div className="value">${Number(estimationResult.tax_amount).toFixed(2)}</div>
      </div>

      <div className="invoice-total-highlight">
        {t('manage_card.total_amount')}{' '}
        <strong>${Number(estimationResult.total_amount || 0).toFixed(2)}</strong>
      </div>
    </>
  ) : (
    <p>{t('manage_card.invoice_not_available')}</p>
  )}
</div>



        </div>
      </div>
    </div>
  );
};

export default ManagePaymentsSection;
